import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildFormValuesFromFields,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
  type DataTableFilter,
  facetedFilterFn,
} from '@folio/components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@folio/components/forms/select';
import { formatPrice } from '@folio/lib';
import type { Asset } from 'korint-sdk';
import type { Field } from 'korint-sdk/react';
import { fieldColumns, fieldFilters, fieldLabel } from '../shared-fields/field-table-columns';
import { perilLabel, perilOptionLabel, tierLabel } from '../shared-quote/labels';
import type {
  AvailablePricingPeril,
  AvailablePricingTier,
} from '../shared-quote/use-available-pricing';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export type FleetAssetSelection = {
  tier: string;
  // Peril id -> chosen state. `option` carries the level for leveled perils.
  perils: Record<string, { included: boolean; option?: string }>;
};
export type FleetSelection = Record<string, FleetAssetSelection>;

type FleetRow = { id: string; label: string; tier: string; values: Record<string, unknown> };

// available-pricing carries per-asset pricing per tier (keyed by entityId); read that,
// not the policy-level breakdown — otherwise every row shows the whole-fleet total.
type TierAssetPricing = {
  assets?: Array<{ entityId: string; totalPremium?: { amountWithFeesAndTaxes?: number } }>;
};

type FleetTierTableProps = {
  assets: Asset[];
  tiers: AvailablePricingTier[];
  productId: string;
  value: FleetSelection;
  onChange: (next: FleetSelection) => void;
  /** Fallback tier for rows not yet seeded (avoids a 0/empty flash before seeding). */
  defaultTier: string;
  /** Resolved asset fields, rendered as filterable columns instead of the plain vehicle column. */
  fields?: Field[];
  disabled?: boolean;
};

export function FleetTierTable({
  assets,
  tiers,
  productId,
  value,
  onChange,
  defaultTier,
  fields,
  disabled,
}: FleetTierTableProps) {
  const { t } = useTranslation();

  const setTierForMany = (assetIds: string[], tierId: string) => {
    const next = { ...value };
    for (const assetId of assetIds) {
      next[assetId] = { ...next[assetId], tier: tierId, perils: {} };
    }
    onChange(next);
  };

  // tierId -> entityId -> price; stable across selections, so the price column is O(1) per row.
  const priceByTierAndAsset = useMemo(() => {
    const byTier = new Map<string, Map<string, number>>();
    for (const tier of tiers) {
      const byAsset = new Map<string, number>();
      for (const entry of (tier.pricing as TierAssetPricing | undefined)?.assets ?? []) {
        byAsset.set(entry.entityId, entry.totalPremium?.amountWithFeesAndTaxes ?? 0);
      }
      byTier.set(tier.tierId, byAsset);
    }
    return byTier;
  }, [tiers]);

  const tierItems = useMemo(
    () =>
      tiers.map(tier => (
        <SelectItem key={tier.tierId} value={tier.tierId}>
          {tierLabel(t, productId, tier.tierId)}
        </SelectItem>
      )),
    [tiers, t, productId],
  );

  // A peril offered by any tier gets a column; rows whose tier doesn't offer it read as empty.
  const { perilsByTier, perilIds } = useMemo(() => {
    const byTier = new Map<string, Map<string, AvailablePricingPeril>>();
    for (const tier of tiers) {
      byTier.set(tier.tierId, new Map((tier.perils ?? []).map(peril => [peril.id, peril])));
    }
    const ids = [...new Set(tiers.flatMap(tier => (tier.perils ?? []).map(({ id }) => id)))];
    return { perilsByTier: byTier, perilIds: ids };
  }, [tiers]);

  // Field resolution (lodash get + dayjs parsing per field) must not re-run on every selection.
  const fieldValuesByAsset = useMemo(
    () =>
      new Map(
        assets.map(asset => [asset.id, fields ? buildFormValuesFromFields(fields, asset) : {}]),
      ),
    [assets, fields],
  );

  // Memoized so ambient parent re-renders (pricing queries, form.watch) don't hand the
  // table new column/data refs — which would remount cells and instantly close an open Select.
  // The chosen tier belongs to the row data: TanStack caches accessor values per row and only
  // drops that cache when `data` changes, so keeping it outside leaves stale prices on screen.
  const rows: FleetRow[] = useMemo(
    () =>
      assets.map(asset => ({
        id: asset.id,
        label: asset.name || (asset.customFields?.registrationNumber as string) || asset.id,
        tier: value[asset.id]?.tier ?? defaultTier,
        values: fieldValuesByAsset.get(asset.id) ?? {},
      })),
    [assets, fieldValuesByAsset, value, defaultTier],
  );

  const perilStateFor = ({ id, tier }: FleetRow, perilId: string) => {
    const peril = perilsByTier.get(tier)?.get(perilId);
    if (!peril) {
      return undefined;
    }
    const chosen = value[id]?.perils?.[perilId];
    const levels = peril.options ?? [];
    return {
      included: chosen?.included ?? peril.included,
      levels,
      level: chosen?.option ?? levels[0],
    };
  };

  const setPerilForMany = (rows: FleetRow[], perilId: string, included: boolean) => {
    const next = { ...value };
    for (const row of rows) {
      next[row.id] = {
        tier: row.tier,
        perils: { ...next[row.id]?.perils, [perilId]: { included } },
      };
    }
    onChange(next);
  };

  const columns: ColumnDef<FleetRow>[] = useMemo(() => {
    const identityColumns: ColumnDef<FleetRow>[] = fields?.length
      ? fieldColumns<FleetRow>({ fields, labelForField: field => fieldLabel(t, field) })
      : [
          {
            id: 'vehicle',
            accessorFn: row => row.label,
            meta: { sticky: 'left' },
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title={t('fleetTierTable.vehicle')} />
            ),
            cell: ({ row }) => (
              <Text inline weight="medium">
                {row.original.label}
              </Text>
            ),
          },
        ];

    const perilColumns: ColumnDef<FleetRow>[] = perilIds.map(perilId => ({
      id: perilId,
      accessorFn: row => {
        const state = perilStateFor(row, perilId);
        if (!state) {
          return '';
        }
        return state.levels.length > 0 ? (state.included && state.level) || '' : state.included;
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={perilLabel(t, productId, perilId)} />
      ),
      cell: ({ row }) => {
        const { id: assetId, tier: tierId } = row.original;
        const state = perilStateFor(row.original, perilId);
        if (!state) {
          return (
            <Text inline tone="muted">
              —
            </Text>
          );
        }

        const { included, levels, level } = state;
        const choose = (peril: { included: boolean; option?: string }) =>
          onChange({
            ...value,
            [assetId]: {
              tier: tierId,
              perils: { ...value[assetId]?.perils, [perilId]: peril },
            },
          });

        return (
          <Group gap={2}>
            <Checkbox
              checked={included}
              onCheckedChange={next =>
                choose({
                  included: next === true,
                  ...(next === true && level && { option: level }),
                })
              }
              disabled={disabled}
              aria-label={perilLabel(t, productId, perilId)}
            />
            {levels.length > 0 && (
              <Select
                value={level}
                onValueChange={option => choose({ included: true, option })}
                disabled={disabled || !included}
              >
                <SelectTrigger className="h-8 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {levels.map(option => (
                    <SelectItem key={option} value={option}>
                      {perilOptionLabel(t, productId, perilId, option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Group>
        );
      },
    }));

    return [
      ...identityColumns,
      {
        id: 'tier',
        accessorFn: row => row.tier,
        filterFn: facetedFilterFn,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('fleetTierTable.tier')} />
        ),
        cell: ({ row }) => (
          <Select
            value={row.original.tier}
            onValueChange={tierId =>
              onChange({
                ...value,
                [row.original.id]: { ...value[row.original.id], tier: tierId, perils: {} },
              })
            }
            disabled={disabled}
          >
            <SelectTrigger className="h-8 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>{tierItems}</SelectContent>
          </Select>
        ),
      },
      ...perilColumns,
      {
        id: 'price',
        accessorFn: row => priceByTierAndAsset.get(row.tier)?.get(row.id) ?? 0,
        meta: { sticky: 'right' },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('fleetTierTable.price')} />
        ),
        cell: ({ getValue }) => (
          <span className="tabular-nums">{`${formatPrice(getValue<number>())} / an`}</span>
        ),
      },
    ];
  }, [
    fields,
    perilIds,
    perilsByTier,
    tierItems,
    priceByTierAndAsset,
    value,
    disabled,
    onChange,
    productId,
    t,
  ]);

  const filters: DataTableFilter[] = useMemo(
    () => [
      ...(fields?.length
        ? fieldFilters({ fields, labelForField: field => fieldLabel(t, field) })
        : []),
      {
        columnId: 'tier',
        title: t('fleetTierTable.tier'),
        type: 'faceted',
        options: tiers.map(({ tierId }) => ({
          label: tierLabel(t, productId, tierId),
          value: tierId,
        })),
      },
    ],
    [fields, tiers, productId, t],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('fleetTierTable.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <DataTable
          columns={columns}
          data={rows}
          dense
          searchable
          filters={filters}
          enableRowSelection
          getRowId={row => row.id}
          renderSelectionActions={(selected, { clearSelection }) => {
            // Only on/off options can be applied in bulk, and only to rows whose tier offers them.
            const bulkPerils = perilIds.flatMap(perilId => {
              const rows = selected.filter(row => {
                const state = perilStateFor(row, perilId);
                return state && state.levels.length === 0;
              });
              if (rows.length === 0) {
                return [];
              }
              const includedCount = rows.filter(
                row => perilStateFor(row, perilId)?.included,
              ).length;
              return [
                {
                  perilId,
                  rows,
                  checked:
                    includedCount === rows.length
                      ? true
                      : includedCount === 0
                        ? false
                        : ('indeterminate' as const),
                },
              ];
            });

            return (
              <Group wrap gap={0} className="gap-x-6 gap-y-2">
                <Group gap={2}>
                  <Text inline size="sm" tone="muted">
                    {t('fleetTierTable.applyTierToSelected')}
                  </Text>
                  <Select
                    onValueChange={tierId => {
                      setTierForMany(
                        selected.map(({ id }) => id),
                        tierId,
                      );
                      clearSelection();
                    }}
                  >
                    <SelectTrigger className="h-8 w-44">
                      <SelectValue placeholder={t('fleetTierTable.chooseTier')} />
                    </SelectTrigger>
                    <SelectContent>{tierItems}</SelectContent>
                  </Select>
                </Group>

                {bulkPerils.length > 0 && (
                  <Group wrap gap={0} className="gap-x-4 gap-y-2">
                    <Text inline size="sm" tone="muted">
                      {t('fleetTierTable.applyOptionsToSelected')}
                    </Text>
                    {bulkPerils.map(({ perilId, rows, checked }) => (
                      <label key={perilId} className="flex items-center gap-1.5 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={next => setPerilForMany(rows, perilId, next === true)}
                          disabled={disabled}
                        />
                        {perilLabel(t, productId, perilId)}
                      </label>
                    ))}
                  </Group>
                )}
              </Group>
            );
          }}
        />
      </CardContent>
    </Card>
  );
}
